import { useEffect } from 'react';

import { notification } from 'antd';
import { useShallow } from 'zustand/shallow';

import { buildChildNodes, updateNodeChildren } from '@/shared/lib/tree';
import type { AssetTreeNode } from '@/shared/lib/tree';

import { useFilterStore, useTreeExpandStore } from '@/entities/discovery';

import { useExperimentExpand } from './useExperimentExpand';
import { useRunExpand } from './useRunExpand';
import { useEntityTypeChildren } from './useTreeChildren';

type UseAssetTreeExpandProps = {
  initialTreeData: AssetTreeNode[];
};

const REPLACE_FROM_INITIAL_ROOT_KEYS = new Set(['category-experiments', 'entityType-glossaryTerm']);

export const mergeTopLevelTreeData = (
  currentTreeData: AssetTreeNode[],
  nextTreeData: AssetTreeNode[]
): AssetTreeNode[] => {
  if (currentTreeData.length === 0) {
    return nextTreeData;
  }

  const currentNodeByKey = new Map(currentTreeData.map((node) => [node.key, node]));

  return nextTreeData.map((nextNode) => {
    const currentNode = currentNodeByKey.get(nextNode.key);

    if (!currentNode || REPLACE_FROM_INITIAL_ROOT_KEYS.has(nextNode.key)) {
      return nextNode;
    }

    const currentChildren = currentNode.children || [];
    const nextChildren = nextNode.children || [];

    if (currentChildren.length > 0 && nextChildren.length === 0) {
      return {
        ...nextNode,
        children: currentChildren,
        isLeaf: currentNode.isLeaf,
        selectable: currentNode.selectable ?? nextNode.selectable,
      };
    }

    return nextNode;
  });
};

export const useAssetTreeExpand = ({ initialTreeData }: UseAssetTreeExpandProps) => {
  const { treeData, expandedKeys, loadingKeys, expandingNode, onExpand, collapseAll } = useTreeExpandStore(
    useShallow((state) => ({
      treeData: state.treeData,
      expandedKeys: state.expandedKeys,
      loadingKeys: state.loadingKeys,
      expandingNode: state.expandingNode,
      onExpand: state.onExpand,
      collapseAll: state.collapseAll,
    }))
  );

  const {
    data: nodeChildren,
    isSuccess: isNodeLoaded,
    isError: isNodeError,
    error: nodeError,
  } = useEntityTypeChildren({
    entityType: expandingNode?.entityType || '',
    index: expandingNode?.index || 'dataAsset',
    useAggregation: expandingNode?.useAggregation || false,
    entityTypes: expandingNode?.entityTypes,
    serviceType: expandingNode?.serviceType,
    serviceName: expandingNode?.serviceName,
    databaseName: expandingNode?.databaseName,
    schemaName: expandingNode?.schemaName,
    enabled: !!expandingNode,
  });

  useExperimentExpand();
  useRunExpand();

  useEffect(() => {
    if (isNodeError && expandingNode) {
      console.error('Failed to load tree children:', nodeError);
      notification.error({
        message: '하위 데이터 로드 실패',
        description: '트리의 하위 항목을 불러오지 못했습니다. 다시 시도해 주세요.',
        duration: 5,
        placement: 'bottomLeft',
      });
      useTreeExpandStore.getState().handleExpandError(expandingNode);
    }
  }, [isNodeError, nodeError, expandingNode]);

  useEffect(() => {
    if (isNodeLoaded && nodeChildren && expandingNode) {
      const serviceType = expandingNode.serviceType?.toLowerCase();

      if (serviceType === 'iceberg') {
        const { loadedNodes } = useTreeExpandStore.getState();

        if (loadedNodes.has(expandingNode.key)) {
          useTreeExpandStore.getState().setExpandingNode(null);
          return;
        }

        const childNodes = buildChildNodes(nodeChildren, expandingNode);

        const store = useTreeExpandStore.getState();
        store.setTreeData(updateNodeChildren(store.treeData, expandingNode.key, childNodes));

        store.addExpandedKey(expandingNode.key);
        store.removeLoadingKey(expandingNode.key);
        store.setExpandingNode(null);
        store.addLoadedNode(expandingNode.key);
      } else {
        useTreeExpandStore.getState().updateNodeChildren(nodeChildren, expandingNode);
      }
    }
  }, [isNodeLoaded, nodeChildren, expandingNode]);

  useEffect(() => {
    if (initialTreeData.length === 0) {
      return;
    }

    const store = useTreeExpandStore.getState();
    const currentTreeData = store.treeData;

    if (currentTreeData.length === 0) {
      store.setTreeData(initialTreeData);
      const firstNode = initialTreeData[0];
      const nodeData = firstNode.data as {
        entityType: string;
        index: string;
        useAggregation: boolean;
        entityTypes?: string[];
      };

      store.setExpandingNode({
        key: firstNode.key,
        entityType: nodeData.entityType,
        index: nodeData.index,
        useAggregation: nodeData.useAggregation,
        entityTypes: nodeData.entityTypes,
      });
      return;
    }

    const mergedTreeData = mergeTopLevelTreeData(currentTreeData, initialTreeData);
    const hasChanged =
      mergedTreeData.length !== currentTreeData.length ||
      mergedTreeData.some((node, index) => node !== currentTreeData[index]);

    if (hasChanged) {
      store.setTreeData(mergedTreeData);
    }
  }, [initialTreeData, treeData.length]);

  useEffect(() => {
    let previousDataSource: string[] = useFilterStore.getState().filters.dataSource;
    let previousAssetType: string[] = useFilterStore.getState().filters.assetType;

    const unsubscribe = useFilterStore.subscribe((state) => {
      const currentDataSource = state.filters.dataSource;
      const currentAssetType = state.filters.assetType;
      const source = state.lastChangeSource;

      const dataSourceChanged =
        currentDataSource.length !== previousDataSource.length ||
        currentDataSource.some((v, i) => v !== previousDataSource[i]);
      const assetTypeChanged =
        currentAssetType.length !== previousAssetType.length ||
        currentAssetType.some((v, i) => v !== previousAssetType[i]);

      if ((dataSourceChanged || assetTypeChanged) && source === 'topBar') {
        useTreeExpandStore.getState().collapseAll();
      }

      previousDataSource = currentDataSource;
      previousAssetType = currentAssetType;
    });

    return () => {
      unsubscribe();
      useTreeExpandStore.getState().resetStore();
    };
  }, []);

  return {
    treeData,
    expandedKeys,
    loadingKeys,
    onExpand,
    collapseAll,
  };
};
